// src/views/PublicBookingPage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { formatPhoneNumber } from '../utils/formatters';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// r9-p6-orgread-8b4f1e27 — this page used to read Firestore directly (an `organizations`
// slug query + a jobTemplates read), which only worked because the security rules made the
// org doc and jobTemplates world-readable — exposing every internal org field to anyone on
// the internet who knew a slug. It now goes through the getPublicBookingInfo callable, which
// returns only what this page renders, so those public read rules could be removed. The
// per-template price is computed server-side (displayPrice) and is null when the operator
// hasn't opted into showing it — raw lineItems no longer reach the browser.

// ─── Reusable Field wrapper ───────────────────────────────────────────────────

const Field = ({ label, required, error, children }) => (
    <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
        {error && <p className="text-xs text-red-600 mt-1 font-medium">{error}</p>}
    </div>
);

const inputClass = (hasError) =>
    `w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors bg-white placeholder:text-gray-400 ${
        hasError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
    }`;

// ─── Main ─────────────────────────────────────────────────────────────────────

const PublicBookingPage = () => {
    const { slug } = useParams();

    const [org, setOrg]           = useState(null);
    const [templates, setTemplates] = useState([]);
    const [pageState, setPageState] = useState('loading');

    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        templateId: '', address: '', notes: '',
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting]   = useState(false);
    const [submitError, setSubmitError] = useState('');

    // ── reCAPTCHA ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!RECAPTCHA_SITE_KEY) return;
        if (document.getElementById('recaptcha-script')) return;
        const script = document.createElement('script');
        script.id  = 'recaptcha-script';
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        document.body.appendChild(script);
        return () => {
            const el = document.getElementById('recaptcha-script');
            if (el) document.body.removeChild(el);
            const badge = document.querySelector('.grecaptcha-badge');
            if (badge) badge.style.display = 'none';
        };
    }, []);

    // ── Load org + bookable templates ─────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await httpsCallable(getFunctions(), 'getPublicBookingInfo')({ slug });
                setOrg({ id: data.orgId, companyName: data.companyName, logoUrl: data.logoUrl });
                setTemplates(data.templates || []);
                setPageState('ready');
            } catch (err) {
                console.error('PublicBookingPage load error:', err);
                setPageState('not_found');
            }
        };
        load();
    }, [slug]);

    // ── Form handlers ─────────────────────────────────────────────────────────
    const set = (field, value) => {
        setForm(p => ({ ...p, [field]: value }));
        if (fieldErrors[field]) setFieldErrors(p => ({ ...p, [field]: '' }));
        if (submitError) setSubmitError('');
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim())                            errs.name       = 'Your name is required';
        if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email address';
        if (!form.phone.trim())                           errs.phone      = 'Phone number is required';
        if (!form.templateId)                             errs.templateId = 'Please select a service';
        if (!form.address.trim())                         errs.address    = 'Service address is required';
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        setSubmitError('');
        try {
            let recaptchaToken = null;
            if (RECAPTCHA_SITE_KEY && window.grecaptcha) {
                recaptchaToken = await window.grecaptcha.execute(
                    RECAPTCHA_SITE_KEY, { action: 'submit_booking' }
                );
            }
            const selected = templates.find(t => t.id === form.templateId);
            await httpsCallable(getFunctions(), 'submitBookingRequest')({
                orgId:        org.id,
                name:         form.name.trim(),
                email:        form.email.trim().toLowerCase(),
                phone:        form.phone.trim(),
                templateId:   form.templateId,
                templateName: selected?.name || '',
                address:      form.address.trim(),
                notes:        form.notes.trim(),
                recaptchaToken,
            });
            setPageState('submitted');
        } catch (err) {
            console.error('submitBookingRequest error:', err);
            setSubmitError('Something went wrong. Please try again or contact the business directly.');
        }
        setSubmitting(false);
    };

    // ── Loading ───────────────────────────────────────────────────────────────
    if (pageState === 'loading') return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-400 font-medium">Loading…</p>
            </div>
        </div>
    );

    // ── Not found ─────────────────────────────────────────────────────────────
    if (pageState === 'not_found') return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="text-center max-w-xs">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h1 className="text-xl font-bold text-gray-800 mb-2">Page Not Found</h1>
                <p className="text-gray-500 text-sm">This booking link may be incorrect or the business may have updated their URL.</p>
            </div>
        </div>
    );

    // ── Success ───────────────────────────────────────────────────────────────
    if (pageState === 'submitted') return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
                {org?.logoUrl
                    ? <img src={org.logoUrl} alt={org.companyName} className="h-14 object-contain mx-auto mb-6" />
                    : <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <span className="text-white text-xl font-bold">{org?.companyName?.charAt(0)?.toUpperCase()}</span>
                      </div>
                }
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                    <strong className="text-gray-700">{org?.companyName}</strong> received your request and will be in touch shortly.
                </p>
                <div className="mt-8 pt-6 border-t border-gray-100">
                    <p className="text-xs text-gray-400">Powered by <span className="font-semibold text-gray-500">Spatchy</span></p>
                </div>
            </div>
        </div>
    );

    // ── Main form ─────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600" />

            <div className="max-w-lg mx-auto px-4 py-10">

                {/* Header */}
                <div className="text-center mb-8">
                    {org?.logoUrl
                        ? <img src={org.logoUrl} alt={org.companyName} className="h-16 object-contain mx-auto mb-4" />
                        : <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <span className="text-white text-2xl font-bold">{org?.companyName?.charAt(0)?.toUpperCase() || '?'}</span>
                          </div>
                    }
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{org?.companyName}</h1>
                    <p className="text-gray-500 mt-1.5 text-sm">Request a Service</p>
                </div>

                <div className="space-y-4">

                    {/* ── Contact card ─────────────────────────────────────── */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Your Info</p>

                        <Field label="Full Name" required error={fieldErrors.name}>
                            <input
                                value={form.name}
                                onChange={e => set('name', e.target.value)}
                                placeholder="Jane Smith"
                                autoComplete="name"
                                className={inputClass(!!fieldErrors.name)}
                            />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Email" required error={fieldErrors.email}>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={e => set('email', e.target.value)}
                                    placeholder="jane@example.com"
                                    autoComplete="email"
                                    className={inputClass(!!fieldErrors.email)}
                                />
                            </Field>
                            <Field label="Phone" required error={fieldErrors.phone}>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={e => set('phone', formatPhoneNumber(e.target.value))}
                                    placeholder="(555) 555-5555"
                                    autoComplete="tel"
                                    className={inputClass(!!fieldErrors.phone)}
                                />
                            </Field>
                        </div>
                    </div>

                    {/* ── Service card ──────────────────────────────────────── */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Service Details</p>

                        <Field label="What do you need done?" required error={fieldErrors.templateId}>
                            {templates.length === 0 ? (
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
                                    <p className="text-sm text-gray-400">No services are currently available for online booking.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {templates.map(t => {
                                        const isSelected = form.templateId === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => set('templateId', t.id)}
                                                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 text-left transition-all active:scale-[0.99] ${
                                                    isSelected
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                                        isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                                                    }`}>
                                                        {isSelected && (
                                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-900' : 'text-gray-800'}`}>
                                                        {t.name}
                                                    </span>
                                                </div>
                                                {t.displayPrice > 0 && (
                                                    <span className={`text-sm font-bold flex-shrink-0 ml-3 ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                                                        ${t.displayPrice.toFixed(2)}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </Field>

                        <Field label="Address" required error={fieldErrors.address}>
                            <input
                                value={form.address}
                                onChange={e => set('address', e.target.value)}
                                placeholder="123 Main St, City, Province"
                                autoComplete="street-address"
                                className={inputClass(!!fieldErrors.address)}
                            />
                        </Field>

                        <Field label="Additional Notes" error={null}>
                            <textarea
                                value={form.notes}
                                onChange={e => set('notes', e.target.value)}
                                placeholder="Access instructions, description of the issue, anything helpful…"
                                rows={3}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 transition-colors bg-white placeholder:text-gray-400 resize-none"
                            />
                        </Field>
                    </div>

                    {/* Error banner */}
                    {submitError && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-red-700">{submitError}</p>
                        </div>
                    )}

                    {/* Submit button */}
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || templates.length === 0}
                        className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl text-base hover:bg-blue-700 active:scale-[0.98] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                        {submitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Submitting…
                            </span>
                        ) : 'Submit'}
                    </button>

                    {/* reCAPTCHA disclosure — required by Google ToS */}
                    <p className="text-xs text-gray-400 text-center leading-relaxed px-2">
                        Protected by reCAPTCHA —{' '}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Privacy</a>
                        {' & '}
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Terms</a>
                    </p>

                    <p className="text-center text-xs text-gray-400 pb-4">
                        Powered by <span className="font-semibold text-gray-500">Spatchy</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PublicBookingPage;